/**
 * Financial Data Service
 * Supports Alpha Vantage and Financial Modeling Prep (FMP) APIs
 * with aggressive IndexedDB caching to respect rate limits.
 */

import type {
  AnnualIncomeStatement,
  AnnualBalanceSheet,
  AnnualCashFlow,
  StockOverview,
  PriceQuote,
  GicsSector,
  ApiProvider,
} from '@/types/stock';

import {
  getCachedIncome, setCachedIncome,
  getCachedBalance, setCachedBalance,
  getCachedCashFlow, setCachedCashFlow,
  getCachedOverview, setCachedOverview,
  getCachedPrice, setCachedPrice,
} from './stockDb';

const CORS_PROXY = 'https://proxy.shakespeare.diy/?url=';

function proxied(url: string): string {
  return `${CORS_PROXY}${encodeURIComponent(url)}`;
}

// ============================================================
// In-memory session cache
// Prevents any repeat API calls within the same browser session,
// even if IndexedDB hasn't persisted yet or the page was just loaded.
// ============================================================

const SESSION_CACHE = new Map<string, unknown>();

function sessionGet<T>(key: string): T | null {
  return SESSION_CACHE.has(key) ? (SESSION_CACHE.get(key) as T) : null;
}

function sessionSet(key: string, value: unknown): void {
  SESSION_CACHE.set(key, value);
}

// ============================================================
// AV rate-limit throttle
// Free tier: 5 calls/minute. We enforce a minimum 13s gap between
// calls so 4 sequential fetches (income + balance + cashflow + overview)
// never exceed the per-minute cap.
// ============================================================

let lastAvCallTime = 0;
const AV_MIN_INTERVAL_MS = 13_000; // 13 seconds between calls

async function avThrottle(): Promise<void> {
  const now = Date.now();
  const elapsed = now - lastAvCallTime;
  if (lastAvCallTime > 0 && elapsed < AV_MIN_INTERVAL_MS) {
    const wait = AV_MIN_INTERVAL_MS - elapsed;
    await new Promise<void>((resolve) => setTimeout(resolve, wait));
  }
  lastAvCallTime = Date.now();
}

// ============================================================
// GICS Sector mapping from Alpha Vantage / FMP strings
// ============================================================

const SECTOR_MAP: Record<string, GicsSector> = {
  'energy': 'Energy',
  'materials': 'Materials',
  'industrials': 'Industrials',
  'consumer discretionary': 'Consumer Discretionary',
  'consumer staples': 'Consumer Staples',
  'health care': 'Health Care',
  'healthcare': 'Health Care',
  'financials': 'Financials',
  'financial services': 'Financials',
  'information technology': 'Information Technology',
  'technology': 'Information Technology',
  'communication services': 'Communication Services',
  'utilities': 'Utilities',
  'real estate': 'Real Estate',
};

function mapSector(raw: string): GicsSector {
  const lower = (raw ?? '').toLowerCase().trim();
  return SECTOR_MAP[lower] ?? 'Unknown';
}

// ============================================================
// Alpha Vantage
// ============================================================

async function avFetch(endpoint: string, params: Record<string, string>, apiKey: string): Promise<unknown> {
  // Enforce 13s gap between AV calls to stay under the 5 calls/minute cap
  await avThrottle();

  // Alpha Vantage supports CORS (Access-Control-Allow-Origin: *),
  // so we call it directly — no proxy needed. The proxy was blocking AV.
  const url = new URL('https://www.alphavantage.co/query');
  url.searchParams.set('function', endpoint);
  url.searchParams.set('apikey', apiKey);
  for (const [k, v] of Object.entries(params)) {
    url.searchParams.set(k, v);
  }

  let res: Response;
  try {
    res = await fetch(url.toString());
  } catch (networkErr) {
    throw new Error(
      `Cannot reach Alpha Vantage (network error). Check your internet connection. Details: ${String(networkErr)}`,
    );
  }

  if (!res.ok) {
    throw new Error(`Alpha Vantage HTTP ${res.status} — try again later.`);
  }

  let data: Record<string, unknown>;
  try {
    data = await res.json() as Record<string, unknown>;
  } catch {
    throw new Error('Alpha Vantage returned an unexpected response (not JSON). Try again.');
  }

  // Always log the raw response keys so devs can diagnose unknown error shapes
  const knownErrorFields = ['Note', 'Information', 'Error Message'];
  const hasError = knownErrorFields.some((f) => f in data);
  if (hasError) {
    console.warn('[Alpha Vantage] Error response:', JSON.stringify(data));
  }

  if (data['Note']) {
    // AV returns Note for BOTH per-minute (5/min) and per-day (25/day) limits.
    // Always include the raw note so the user/dev can see the exact message.
    const note = String(data['Note']);
    if (note.toLowerCase().includes('minute')) {
      throw new Error(
        `Alpha Vantage per-minute limit hit (5 calls/minute on the free tier). ` +
        `Please wait 60 seconds and try again.\n\nAV message: "${note}"`,
      );
    }
    throw new Error(
      `Alpha Vantage rate limit: "${note}"\n\n` +
      `Free tier allows 25 calls/day. Data is cached for 24 hours — ` +
      `try again tomorrow or upload a CSV file instead.`,
    );
  }

  if (data['Information']) {
    const info = String(data['Information']);
    if (info.includes('demo') || info.includes('claim your free API key')) {
      throw new Error(
        'Your Alpha Vantage API key is a demo key or is invalid. ' +
        'Get a free key at alphavantage.co and enter it in Settings.',
      );
    }
    if (info.toLowerCase().includes('premium') || info.toLowerCase().includes('subscribe')) {
      throw new Error(
        `This Alpha Vantage endpoint requires a premium plan.\n\nAV message: "${info}"`,
      );
    }
    // Surface every other Information message verbatim
    throw new Error(`Alpha Vantage: "${info}"`);
  }

  if (data['Error Message']) {
    throw new Error(`Alpha Vantage error: "${String(data['Error Message'])}" — check the ticker symbol.`);
  }

  return data;
}

function safeNum(val: unknown): number {
  const n = Number(val);
  return isNaN(n) ? 0 : n;
}

function safeNumOrNull(val: unknown): number | null {
  if (val === null || val === undefined || val === 'None' || val === '-') return null;
  const n = Number(val);
  return isNaN(n) ? null : n;
}

export async function avGetIncome(ticker: string, apiKey: string): Promise<AnnualIncomeStatement[]> {
  const sessionKey = `income:${ticker}`;
  const sessionHit = sessionGet<AnnualIncomeStatement[]>(sessionKey);
  if (sessionHit) return sessionHit;

  const cached = await getCachedIncome(ticker);
  if (cached) { sessionSet(sessionKey, cached); return cached; }

  const data = await avFetch('INCOME_STATEMENT', { symbol: ticker }, apiKey) as Record<string, unknown>;
  const annual = (data['annualReports'] ?? []) as Array<Record<string, string>>;

  const result: AnnualIncomeStatement[] = annual.map((r) => {
    const revenue = safeNum(r['totalRevenue']);
    const netIncome = safeNum(r['netIncome']);
    const sharesOutstanding = safeNum(r['commonStockSharesOutstanding']) || 1;
    return {
      fiscalYear: new Date(r['fiscalDateEnding']).getFullYear(),
      revenue,
      grossProfit: safeNum(r['grossProfit']),
      operatingIncome: safeNum(r['operatingIncome']),
      netIncome,
      eps: safeNum(r['reportedEPS']) || (sharesOutstanding > 0 ? netIncome / sharesOutstanding : 0),
      sharesOutstanding,
    };
  }).sort((a, b) => a.fiscalYear - b.fiscalYear);

  sessionSet(sessionKey, result);
  await setCachedIncome(ticker, result);
  return result;
}

export async function avGetBalance(ticker: string, apiKey: string): Promise<AnnualBalanceSheet[]> {
  const sessionKey = `balance:${ticker}`;
  const sessionHit = sessionGet<AnnualBalanceSheet[]>(sessionKey);
  if (sessionHit) return sessionHit;

  const cached = await getCachedBalance(ticker);
  if (cached) { sessionSet(sessionKey, cached); return cached; }

  const data = await avFetch('BALANCE_SHEET', { symbol: ticker }, apiKey) as Record<string, unknown>;
  const annual = (data['annualReports'] ?? []) as Array<Record<string, string>>;

  const result: AnnualBalanceSheet[] = annual.map((r) => ({
    fiscalYear: new Date(r['fiscalDateEnding']).getFullYear(),
    currentAssets: safeNum(r['totalCurrentAssets']),
    currentLiabilities: safeNum(r['totalCurrentLiabilities']),
    totalAssets: safeNum(r['totalAssets']),
    totalLiabilities: safeNum(r['totalLiabilities']),
    totalEquity: safeNum(r['totalShareholderEquity']),
    longTermDebt: safeNum(r['longTermDebt']),
    cash: safeNum(r['cashAndCashEquivalentsAtCarryingValue']),
  })).sort((a, b) => a.fiscalYear - b.fiscalYear);

  sessionSet(sessionKey, result);
  await setCachedBalance(ticker, result);
  return result;
}

export async function avGetCashFlow(ticker: string, apiKey: string): Promise<AnnualCashFlow[]> {
  const sessionKey = `cashflow:${ticker}`;
  const sessionHit = sessionGet<AnnualCashFlow[]>(sessionKey);
  if (sessionHit) return sessionHit;

  const cached = await getCachedCashFlow(ticker);
  if (cached) { sessionSet(sessionKey, cached); return cached; }

  const data = await avFetch('CASH_FLOW', { symbol: ticker }, apiKey) as Record<string, unknown>;
  const annual = (data['annualReports'] ?? []) as Array<Record<string, string>>;

  const result: AnnualCashFlow[] = annual.map((r) => {
    const operatingCashFlow = safeNum(r['operatingCashflow']);
    const capex = safeNum(r['capitalExpenditures']);
    return {
      fiscalYear: new Date(r['fiscalDateEnding']).getFullYear(),
      operatingCashFlow,
      capitalExpenditures: capex,
      freeCashFlow: operatingCashFlow - Math.abs(capex),
      dividendsPaid: safeNum(r['dividendPayout']),
    };
  }).sort((a, b) => a.fiscalYear - b.fiscalYear);

  sessionSet(sessionKey, result);
  await setCachedCashFlow(ticker, result);
  return result;
}

export async function avGetOverview(ticker: string, apiKey: string): Promise<StockOverview> {
  const sessionKey = `overview:${ticker}`;
  const sessionHit = sessionGet<StockOverview>(sessionKey);
  if (sessionHit) return sessionHit;

  const cached = await getCachedOverview(ticker);
  if (cached) { sessionSet(sessionKey, cached); return cached; }

  const data = await avFetch('OVERVIEW', { symbol: ticker }, apiKey) as Record<string, string>;

  const result: StockOverview = {
    ticker: data['Symbol'] ?? ticker,
    name: data['Name'] ?? ticker,
    exchange: data['Exchange'] ?? '',
    sector: mapSector(data['Sector'] ?? ''),
    industry: data['Industry'] ?? '',
    marketCap: safeNum(data['MarketCapitalization']),
    peRatio: safeNumOrNull(data['PERatio']),
    pbRatio: safeNumOrNull(data['PriceToBookRatio']),
    dividendYield: safeNumOrNull(data['DividendYield']),
    beta: safeNumOrNull(data['Beta']),
    description: data['Description'] ?? '',
    country: data['Country'] ?? '',
    currency: data['Currency'] ?? 'USD',
  };

  sessionSet(sessionKey, result);
  await setCachedOverview(ticker, result);
  return result;
}

export async function avGetPrice(ticker: string, apiKey: string): Promise<PriceQuote> {
  const cached = await getCachedPrice(ticker);
  if (cached) return cached;

  const data = await avFetch('GLOBAL_QUOTE', { symbol: ticker }, apiKey) as Record<string, Record<string, string>>;
  const q = data['Global Quote'] ?? {};

  const price = safeNum(q['05. price']);
  const change = safeNum(q['09. change']);
  const result: PriceQuote = {
    ticker,
    price,
    change,
    changePercent: safeNum(q['10. change percent']?.replace('%', '')),
    volume: safeNum(q['06. volume']),
    marketCap: 0, // not available in quote endpoint
    timestamp: Date.now(),
  };

  await setCachedPrice(ticker, result);
  return result;
}

// ============================================================
// Financial Modeling Prep (FMP)
// Uses the /stable/ base path (FMP's current recommended API).
// Endpoint format: /stable/<endpoint>?symbol=TICKER&...&apikey=KEY
// ============================================================

async function fmpFetch(endpoint: string, params: Record<string, string>, apiKey: string): Promise<unknown> {
  const url = new URL(`https://financialmodelingprep.com/stable/${endpoint}`);
  url.searchParams.set('apikey', apiKey);
  for (const [k, v] of Object.entries(params)) {
    url.searchParams.set(k, v);
  }
  const res = await fetch(proxied(url.toString()));
  if (!res.ok) {
    if (res.status === 401) {
      throw new Error('FMP API key is invalid or expired. Please check your key in Settings.');
    }
    if (res.status === 403) {
      throw new Error(
        'FMP plan does not include this endpoint (HTTP 403). ' +
        'Financial statements require a paid FMP plan. ' +
        'Use Alpha Vantage (free, 25 calls/day) instead, or upload CSV files manually.',
      );
    }
    throw new Error(`FMP HTTP ${res.status}`);
  }
  const data = await res.json() as unknown;
  // FMP returns {"Error Message": "..."} on auth/plan errors even with HTTP 200
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const errMsg = (data as Record<string, unknown>)['Error Message'] as string | undefined;
    if (errMsg) {
      if (errMsg.toLowerCase().includes('invalid api')) {
        throw new Error('FMP API key is invalid. Please check your key in Settings.');
      }
      throw new Error(`FMP error: ${errMsg}`);
    }
  }
  if (Array.isArray(data) && data.length === 0) {
    throw new Error(`FMP returned no data for this ticker. It may be invalid or not covered by your plan.`);
  }
  return data;
}

export async function fmpGetIncome(ticker: string, apiKey: string): Promise<AnnualIncomeStatement[]> {
  const cached = await getCachedIncome(ticker);
  if (cached) return cached;

  const data = await fmpFetch('income-statement', { symbol: ticker, period: 'annual', limit: '10' }, apiKey) as Array<Record<string, unknown>>;

  const result: AnnualIncomeStatement[] = data.map((r) => {
    const revenue = safeNum(r['revenue']);
    const netIncome = safeNum(r['netIncome']);
    const sharesOutstanding = safeNum(r['weightedAverageShsOutDil']) || 1;
    return {
      fiscalYear: new Date(r['date'] as string).getFullYear(),
      revenue,
      grossProfit: safeNum(r['grossProfit']),
      operatingIncome: safeNum(r['operatingIncome']),
      netIncome,
      eps: safeNum(r['eps']) || safeNum(r['epsdiluted']) || (sharesOutstanding > 0 ? netIncome / sharesOutstanding : 0),
      sharesOutstanding,
    };
  }).sort((a, b) => a.fiscalYear - b.fiscalYear);

  await setCachedIncome(ticker, result);
  return result;
}

export async function fmpGetBalance(ticker: string, apiKey: string): Promise<AnnualBalanceSheet[]> {
  const cached = await getCachedBalance(ticker);
  if (cached) return cached;

  const data = await fmpFetch('balance-sheet-statement', { symbol: ticker, period: 'annual', limit: '10' }, apiKey) as Array<Record<string, unknown>>;

  const result: AnnualBalanceSheet[] = data.map((r) => ({
    fiscalYear: new Date(r['date'] as string).getFullYear(),
    currentAssets: safeNum(r['totalCurrentAssets']),
    currentLiabilities: safeNum(r['totalCurrentLiabilities']),
    totalAssets: safeNum(r['totalAssets']),
    totalLiabilities: safeNum(r['totalLiabilities']),
    totalEquity: safeNum(r['totalStockholdersEquity']),
    longTermDebt: safeNum(r['longTermDebt']),
    cash: safeNum(r['cashAndCashEquivalents']),
  })).sort((a, b) => a.fiscalYear - b.fiscalYear);

  await setCachedBalance(ticker, result);
  return result;
}

export async function fmpGetCashFlow(ticker: string, apiKey: string): Promise<AnnualCashFlow[]> {
  const cached = await getCachedCashFlow(ticker);
  if (cached) return cached;

  const data = await fmpFetch('cash-flow-statement', { symbol: ticker, period: 'annual', limit: '10' }, apiKey) as Array<Record<string, unknown>>;

  const result: AnnualCashFlow[] = data.map((r) => {
    const operatingCashFlow = safeNum(r['operatingCashFlow']);
    const capex = safeNum(r['capitalExpenditure']);
    return {
      fiscalYear: new Date(r['date'] as string).getFullYear(),
      operatingCashFlow,
      capitalExpenditures: capex,
      freeCashFlow: safeNum(r['freeCashFlow']) || (operatingCashFlow - Math.abs(capex)),
      dividendsPaid: safeNum(r['dividendsPaid']),
    };
  }).sort((a, b) => a.fiscalYear - b.fiscalYear);

  await setCachedCashFlow(ticker, result);
  return result;
}

export async function fmpGetOverview(ticker: string, apiKey: string): Promise<StockOverview> {
  const cached = await getCachedOverview(ticker);
  if (cached) return cached;

  const data = await fmpFetch('profile', { symbol: ticker }, apiKey) as Array<Record<string, unknown>>;
  const p = data[0] ?? {};

  const result: StockOverview = {
    ticker: (p['symbol'] as string) ?? ticker,
    name: (p['companyName'] as string) ?? ticker,
    exchange: (p['exchangeShortName'] as string) ?? '',
    sector: mapSector((p['sector'] as string) ?? ''),
    industry: (p['industry'] as string) ?? '',
    marketCap: safeNum(p['mktCap']),
    peRatio: safeNumOrNull(p['pe']),
    pbRatio: null,
    dividendYield: safeNumOrNull(p['lastDiv']),
    beta: safeNumOrNull(p['beta']),
    description: (p['description'] as string) ?? '',
    country: (p['country'] as string) ?? '',
    currency: (p['currency'] as string) ?? 'USD',
  };

  await setCachedOverview(ticker, result);
  return result;
}

export async function fmpGetPrice(ticker: string, apiKey: string): Promise<PriceQuote> {
  const cached = await getCachedPrice(ticker);
  if (cached) return cached;

  const data = await fmpFetch('quote', { symbol: ticker }, apiKey) as Array<Record<string, unknown>>;
  const q = data[0] ?? {};

  const result: PriceQuote = {
    ticker,
    price: safeNum(q['price']),
    change: safeNum(q['change']),
    changePercent: safeNum(q['changesPercentage']),
    volume: safeNum(q['volume']),
    marketCap: safeNum(q['marketCap']),
    timestamp: Date.now(),
  };

  await setCachedPrice(ticker, result);
  return result;
}

// ============================================================
// Unified API facade
// ============================================================

export interface FinancialData {
  income: AnnualIncomeStatement[];
  balance: AnnualBalanceSheet[];
  cashFlow: AnnualCashFlow[];
  overview: StockOverview;
}

export async function fetchFinancialData(
  ticker: string,
  provider: ApiProvider,
  alphaVantageKey: string,
  fmpKey: string,
  onStep?: (step: string) => void,
): Promise<FinancialData> {
  const t = ticker.toUpperCase();

  if (provider === 'alpha_vantage') {
    // Run sequentially with a 13s throttle between live API calls.
    // Cached calls (session or IndexedDB) return instantly — no delay, no quota used.
    onStep?.('Fetching income statements…');
    const income = await avGetIncome(t, alphaVantageKey);
    onStep?.('Fetching balance sheet… (rate-limiting: up to 13s between calls)');
    const balance = await avGetBalance(t, alphaVantageKey);
    onStep?.('Fetching cash flow statement…');
    const cashFlow = await avGetCashFlow(t, alphaVantageKey);
    onStep?.('Fetching company overview…');
    const overview = await avGetOverview(t, alphaVantageKey);
    onStep?.('Building report…');
    return { income, balance, cashFlow, overview };
  }

  if (provider === 'fmp') {
    onStep?.('Fetching from FMP…');
    const [income, balance, cashFlow, overview] = await Promise.all([
      fmpGetIncome(t, fmpKey),
      fmpGetBalance(t, fmpKey),
      fmpGetCashFlow(t, fmpKey),
      fmpGetOverview(t, fmpKey),
    ]);
    return { income, balance, cashFlow, overview };
  }

  throw new Error('No API provider configured. Please add an API key in Settings.');
}

export async function fetchPrice(
  ticker: string,
  provider: ApiProvider,
  alphaVantageKey: string,
  fmpKey: string,
): Promise<PriceQuote> {
  const t = ticker.toUpperCase();
  if (provider === 'alpha_vantage') return avGetPrice(t, alphaVantageKey);
  if (provider === 'fmp') return fmpGetPrice(t, fmpKey);
  throw new Error('No API provider configured.');
}
